import React, { useCallback, useEffect, useState } from 'react';
import CreateEventWizard from '../../features/events/components/CreateEventWizard';
import { cloneEvent, fetchEvent, fetchEvents, updateEvent } from '../../features/events/services/eventService';
import { getEventType, EVENT_STATUS_LABELS } from '../../features/events/constants/eventTypes';
import { useDispatch } from 'react-redux';
import { addNotification } from '../../store/slices/uiSlice';

const MaximizeIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
  </svg>
);

const MinimizeIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9H5V5m14 5h-4V5m-4 14h4v-4M5 19h4v-4" />
  </svg>
);

const statusColor = (status) => {
  switch (status) {
    case 'WAITING_FOR_RESPONSE': return 'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-200';
    case 'CONFIRMED':
    case 'IN_PROGRESS': return 'bg-blue-100 text-blue-800 dark:bg-blue-950/50 dark:text-blue-200';
    case 'COMPLETED':
    case 'CLOSED': return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200';
    case 'CANCELLED':
    case 'EXPIRED': return 'bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-200';
    case 'DRAFT': return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200';
    default: return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200';
  }
};

const formatSchedule = (schedule) => {
  if (!schedule) return 'No schedule';
  const { startDate, startTime, endTime, allDay } = schedule;
  if (!startDate) return 'No schedule';
  if (allDay) return `${startDate} · All day`;
  return `${startDate}${startTime ? ` · ${startTime}` : ''}${endTime ? ` – ${endTime}` : ''}`;
};

const EventsPanel = ({
  channel,
  otherUser,
  onClose,
  isExpanded = false,
  onToggleExpand,
}) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [showWizard, setShowWizard] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [cloningId, setCloningId] = useState(null);
  const [publishingId, setPublishingId] = useState(null);
  const dispatch = useDispatch();

  const canEditEvent = (event) => event?.status !== 'DRAFT'
    && !['CANCELLED', 'EXPIRED'].includes(event?.status);

  const openNewWizard = () => {
    setEditingEvent(null);
    setShowWizard(true);
  };

  const openEditWizard = (event) => {
    setEditingEvent(event);
    setShowWizard(true);
  };

  const closeWizard = () => {
    setShowWizard(false);
    setEditingEvent(null);
  };

  const handleClone = async (event, e) => {
    e?.stopPropagation?.();
    if (!event?.id || cloningId) return;
    setCloningId(event.id);
    try {
      const cloned = await cloneEvent(event.id);
      dispatch(addNotification({
        type: 'success',
        message: `"${cloned.name}" saved as draft`,
      }));
      await loadEvents({ silent: true });
      setEditingEvent(cloned);
      setShowWizard(true);
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: err?.response?.data?.error || 'Failed to clone event',
      }));
    } finally {
      setCloningId(null);
    }
  };

  const handlePublishDraft = async (event, e) => {
    e?.stopPropagation?.();
    if (!event?.id || publishingId) return;
    setPublishingId(event.id);
    try {
      await updateEvent(event.id, { draft: false });
      dispatch(addNotification({
        type: 'success',
        message: `"${event.name}" published`,
      }));
      await loadEvents({ silent: true });
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: err?.response?.data?.error || 'Failed to publish event',
      }));
    } finally {
      setPublishingId(null);
    }
  };

  const handleEditPublished = async (event, e) => {
    e?.stopPropagation?.();
    if (!event?.id) return;
    try {
      const fresh = await fetchEvent(event.id);
      setEditingEvent(fresh);
      setShowWizard(true);
    } catch (err) {
      dispatch(addNotification({
        type: 'error',
        message: err?.response?.data?.error || 'Failed to load event',
      }));
    }
  };

  const loadEvents = useCallback(async ({ silent = false } = {}) => {
    if (!channel?.id) {
      setEvents([]);
      return;
    }
    if (silent) setRefreshing(true);
    else setLoading(true);
    setError('');
    try {
      const data = await fetchEvents({ channelId: channel.id });
      setEvents(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to load events');
      setEvents([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [channel?.id]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const draftCount = events.filter((e) => e.status === 'DRAFT').length;
  const activeCount = events.filter((e) => !['DRAFT', 'CLOSED', 'CANCELLED', 'EXPIRED'].includes(e.status)).length;

  return (
    <div className="flex flex-col h-full min-h-0 bg-white dark:bg-gray-800">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        <div className="flex items-center space-x-2 min-w-0">
          <svg className="w-5 h-5 text-primary-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Events</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {events.length} total · {activeCount} active{draftCount ? ` · ${draftCount} draft` : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <button
            type="button"
            onClick={openNewWizard}
            className="px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-primary-600 text-white hover:bg-primary-700 transition-colors"
          >
            + New
          </button>
          <button
            type="button"
            onClick={() => loadEvents({ silent: true })}
            disabled={loading || refreshing || !channel?.id}
            className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
            title="Refresh events"
            aria-label="Refresh events"
          >
            <svg className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
          {typeof onToggleExpand === 'function' && (
            <button
              type="button"
              onClick={onToggleExpand}
              className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              title={isExpanded ? 'Shrink events' : 'Enlarge events'}
              aria-label={isExpanded ? 'Shrink events' : 'Enlarge events'}
              aria-pressed={isExpanded}
            >
              {isExpanded ? <MinimizeIcon /> : <MaximizeIcon />}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            title="Close panel"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0 p-3 space-y-2">
        {loading && (
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">Loading events…</p>
        )}
        {!loading && error && (
          <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}
        {!loading && !error && events.length === 0 && (
          <div className="text-center py-10 px-4">
            <p className="text-sm font-medium text-gray-900 dark:text-white">No events yet</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Create attendance, polls, meetings, and more.</p>
            <button
              type="button"
              onClick={openNewWizard}
              className="mt-4 px-4 py-2 text-sm font-semibold rounded-lg bg-primary-600 text-white hover:bg-primary-700"
            >
              Create event
            </button>
          </div>
        )}
        {!loading && events.map((event) => {
          const typeMeta = getEventType(event.eventType);
          const isDraft = event.status === 'DRAFT';
          return (
            <article
              key={event.id}
              role={isDraft ? 'button' : undefined}
              tabIndex={isDraft ? 0 : undefined}
              onClick={isDraft ? () => openEditWizard(event) : undefined}
              onKeyDown={isDraft ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openEditWizard(event);
                }
              } : undefined}
              className={`rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40 p-3 transition-colors ${
                isDraft
                  ? 'hover:border-primary-400 dark:hover:border-primary-600 cursor-pointer hover:bg-primary-50/50 dark:hover:bg-primary-950/20'
                  : 'hover:border-primary-300 dark:hover:border-primary-700'
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-lg" aria-hidden="true">{typeMeta?.icon || '📅'}</span>
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white truncate">{event.name}</h4>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{typeMeta?.label || event.eventType}</p>
                  <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">{formatSchedule(event.schedule)}</p>
                  {event.description && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">{event.description}</p>
                  )}
                </div>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${statusColor(event.status)}`}>
                  {EVENT_STATUS_LABELS[event.status] || event.status}
                </span>
              </div>
              <div className="flex items-center justify-between mt-2 gap-2">
                <p className="text-[10px] text-gray-400">
                  {(event.participants || []).length} participant{(event.participants || []).length === 1 ? '' : 's'}
                </p>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {isDraft && (
                    <button
                      type="button"
                      onClick={(e) => handlePublishDraft(event, e)}
                      disabled={publishingId === event.id}
                      className="text-[10px] font-semibold px-2 py-1 rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 transition-colors"
                    >
                      {publishingId === event.id ? 'Publishing…' : 'Publish'}
                    </button>
                  )}
                  {canEditEvent(event) && (
                    <button
                      type="button"
                      onClick={(e) => handleEditPublished(event, e)}
                      className="text-[10px] font-semibold px-2 py-1 rounded-md text-primary-700 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-950/30 transition-colors"
                    >
                      Edit
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={(e) => handleClone(event, e)}
                    disabled={cloningId === event.id}
                    className="text-[10px] font-semibold px-2 py-1 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
                  >
                    {cloningId === event.id ? 'Cloning…' : 'Clone'}
                  </button>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      <CreateEventWizard
        isOpen={showWizard}
        onClose={closeWizard}
        onCreated={() => loadEvents({ silent: true })}
        channel={channel}
        otherUser={otherUser}
        editingEvent={editingEvent}
      />
    </div>
  );
};

export default EventsPanel;
